import React, {useEffect, useState} from "react";
import {Helmet} from "react-helmet";
import {useDispatch} from "react-redux";
import {LoaderHide, LoaderShow} from "../../Store/Actions/Loader";
import {showToast} from "../../Store/Actions/ToastAction";
import Api from "../../API/api";
import Toggle from "../../UI/Toggle";
import { Input } from "../../UI/Input/index.js";
import { CustomSelect as Select } from "../../UI/Select/index.js";

export const Users = () => {
  const dispatch = useDispatch()
  const [users, setUsers] = useState([])
  const [searchQuery, setSearchQuery] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalUsers, setTotalUsers] = useState(0)
  const [totalPages, setTotalPages] = useState(0)

  useEffect(() => {
    getUsers()
  }, [currentPage, searchQuery]) // eslint-disable-line react-hooks/exhaustive-deps

  const getUsers = () => {
    dispatch(LoaderShow())
    const params = new URLSearchParams()
    if (searchQuery) params.append('search', searchQuery)
    params.append('page', currentPage)
    params.append('perPage', 10)
    
    Api.getData(`admin/users-list?${params.toString()}`, localStorage.getItem('userToken'))
      .then(res => {
        dispatch(LoaderHide())
        setUsers(res.data.items || res.data.users || res.data)
        setTotalUsers(res.data.totalItems || res.data.total || res.data.length)
        setTotalPages(res.data.totalPages || Math.ceil((res.data.totalItems || res.data.total || res.data.length) / 10))
      })
      .catch((err) => {
        dispatch(LoaderHide())
        dispatch(showToast({
          title: "Error",
          text: 'Failed to fetch users',
          type: "error",
        }))
      })
  }

  const handleSearchChange = (e) => {
    console.log(e.target.value)
    setSearchQuery(e.target.value)
    setCurrentPage(1) // Reset to first page when searching
  }

  const handlePageChange = (page) => {
    setCurrentPage(page)
  }

  const renderPagination = () => {
    const pages = []
    const maxVisiblePages = 5
    
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2))
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1)
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1)
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          className={`pagination__btn ${currentPage === i ? 'active' : ''}`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>
      )
    }

    return (
      <div className="pagination">
        <button
          className="pagination__btn"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        {pages}
        <button
          className="pagination__btn"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>
    )
  }

  const renderUsers = () => {
    return users.map(user => (
      <div className="users__table-th" key={user.id}>
        <span>{user?.externalId}</span>
        <span className="table__img"><img src={user?.profilePhotoUrl === null ? 'assets/default-photo.jpeg' : user?.profilePhotoUrl} alt="User profile"/></span>
        <span>{user?.name}</span>
        <span>{user?.playerId}</span>
        <span>{user?.points} point(s)</span>
        <span>
          <AdSwitcher user={user}/>
        </span>
        <span>
          <GodSwitcher user={user}/>
        </span>
      </div>
    ))
  }

  return (
    <div className='page'>
      <Helmet>
        <meta charSet="utf-8" />
        <title>SARK ADMIN | HOME</title>
      </Helmet>
      <div className="users container">
        <div className="users__header">
          <p className="users__title">Users <span>Total: {totalUsers} user(s)</span></p>
          <div className="users__controls">
            <div className="users__search">
              <Input
                type="text"
                placeholder="Search users..."
                value={searchQuery}
                onChange={handleSearchChange}
                className="search-input"
              />
            </div>
          </div>
        </div>
        <div className="users__table-head">
          <span>ID</span>
          <span>PHOTO</span>
          <span>NAME</span>
          <span>PLAYER ID</span>
          <span>POINTS</span>
          <span>ADS PURCHASE</span>
          <span>GOTD</span>
        </div>
        {renderUsers()}
        {totalPages > 1 && (
          <div className="users__pagination">
            {renderPagination()}
          </div>
        )}
      </div>
    </div>
  )
}

const GodSwitcher = ({ user }) => {
  const dispatch = useDispatch();
  const [userSubscriptionToggles, setUserSubscriptionToggles] = useState(user?.godLimitExtended || false);

  useEffect(() => {
    setUserSubscriptionToggles(user?.godLimitExtended || false);
  }, [user?.godLimitExtended]);

  const handleSubscriptionToggle = () => {
    const newToggleState = !userSubscriptionToggles;
    setUserSubscriptionToggles(newToggleState);
    
    // Send API request to toggle GOD limit
    Api.postData(
      `admin/toggle-god-limit/${user.id}`, 
      { godLimitExtended: newToggleState }, // true when GOD is disabled, false when enabled
      localStorage.getItem('userToken')
    )
      .then(res => {
        dispatch(showToast({
          title: "GOD Toggle",
          text: 'GOD limit updated successfully',
          type: "success",
        }));
      })
      .catch((err) => {
        dispatch(showToast({
          title: "GOD Toggle",
          text: err.response?.data?.message || 'Error updating GOD toggle',
          type: "error",
        }));
        // Revert the toggle state if the request fails
        setUserSubscriptionToggles(!newToggleState);
      });
  }

  return (
    <Toggle 
      isOn={userSubscriptionToggles}
      onToggle={handleSubscriptionToggle}
      disabled={!!user?.godLimitPurchaseDate}
    />
  )
}

const AdSwitcher = ({ user }) => {
  const dispatch = useDispatch();
  const [userAdToggles, setUserAdToggles] = useState(user?.addsDisabled || false);

  useEffect(() => {
    setUserAdToggles(user?.addsDisabled || false);
  }, [user?.addsDisabled]);

  const handleAdToggle = () => {
    const newToggleState = !userAdToggles;
    setUserAdToggles(newToggleState);
    
    // Send API request to toggle ads
    Api.postData(
      `admin/toggle-adds/${user.id}`, 
      { addsDisabled: newToggleState }, // true when ads are disabled, false when enabled
      localStorage.getItem('userToken')
    )
      .then(res => {
        dispatch(showToast({
          title: "Ads Toggle",
          text: 'Ads settings updated successfully',
          type: "success",
        }));
      })
      .catch((err) => {
        dispatch(showToast({
          title: "Ads Toggle",
          text: err.response?.data?.message || 'Error updating ads toggle',
          type: "error",
        }));
        // Revert the toggle state if the request fails
        setUserAdToggles(!newToggleState);
      });
  }

  return (
    <Toggle 
      isOn={userAdToggles}
      onToggle={handleAdToggle}
      disabled={!!user?.disableAddsPurchaseDate}
    />
  )
}